import React from "react";
import { type Banner, type EventsPageSimple, type EventSeries } from "lib/sanity";
import { Page } from "components/page";
import { Section } from "components/section";
import { Box, List, ListItem, SimpleGrid, Text } from "@chakra-ui/react";
import { Link } from "ui/link";
import { HalfHero } from "components/half-hero";
import { eventNewsletterCta } from "components/events-newsletter-form";
import { formatDateWithYear } from "utils";
import ReactMarkdown from "react-markdown";
import { TextTitle } from "ui/text";
import { Button } from "ui/button";

type SeriesProps = {
  associatedEventPages?: EventsPageSimple[];
  announcementBanner?: Banner;
} & EventSeries;

function EventSeriesTemplate({
  title,
  excerpt,
  imageUrl,
  description,
  startDate,
  endDate,
  location,
  metaTitle,
  metaDescription,
  metaImageUrl,
  associatedEventPages,
  announcementBanner,
}: SeriesProps) {
  const formattedStartDate = formatDateWithYear(startDate);
  const formattedEndDate = endDate ? formatDateWithYear(endDate) : undefined;
  const seriesDates = formattedStartDate + (formattedEndDate ? ` - ${formattedEndDate}` : "");

  return (
    <Page
      announcementBanner={announcementBanner}
      title={metaTitle}
      description={
        metaDescription ??
        excerpt ??
        "Hightouch helps you easily and efficiently sync data from your warehouse to your SaaS tools without scripts, CSVs or a CDP."
      }
      image={
        metaImageUrl
          ? {
              width: 1200,
              height: 630,
              url: metaImageUrl,
              alt: metaTitle,
            }
          : undefined
      }
      footerOptions={{
        ctaProps: eventNewsletterCta,
      }}
    >
      <HalfHero
        hideCTA
        eyebrow={seriesDates}
        title={title}
        description={
          <Box textStyle="article.p">
            <ReactMarkdown
              components={{
                a: ({ node, href, ...props }) => <Link href={href} variant="inlineDark" {...props} />,
                p: ({ node, ...props }) => <Text mb={4} {...props} />,
                li: ({ children }) => <ListItem textStyle="article.listItem">{children}</ListItem>,
                ol: ({ children }) => <List textStyle="article.list">{children}</List>,
                ul: ({ children }) => (
                  <List textStyle="article.list" listStyleType="disc">
                    {children}
                  </List>
                ),
              }}
            >
              {description}
            </ReactMarkdown>
          </Box>
        }
        image={imageUrl + "?w=1200"}
        imageRatio={3 / 2}
      />

      {associatedEventPages && associatedEventPages.length > 0 && (
        <Section>
          <TextTitle size="lg" title="Find an event" />

          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, xl: 4 }}
            gap={{ base: "smaller", md: "small", xl: "small" }}
            mt={{ base: 8, md: 16 }}
          >
            {associatedEventPages
              ?.sort((a, b) => {
                return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
              })
              .map((eventPage) => (
                <Box
                  key={eventPage.slug}
                  bg="brand.backgroundSecondary.light"
                  borderRadius="base"
                  p={{ base: "small", md: "big" }}
                >
                  <TextTitle
                    size="xs"
                    chip={{
                      text: `${formatDateWithYear(eventPage.startDate)}${
                        eventPage.endDate && eventPage.endDate !== eventPage.startDate
                          ? ` - ${formatDateWithYear(eventPage.endDate)}`
                          : ""
                      }`,
                    }}
                    title={eventPage.seriesCardTitle}
                    actions={
                      <Button hasRightChevron href={`/events/${eventPage.slug}`}>
                        Register now
                      </Button>
                    }
                  />
                </Box>
              ))}
          </SimpleGrid>
        </Section>
      )}
    </Page>
  );
}

export default EventSeriesTemplate;
